import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { Provider as StoreProvider } from 'react-redux';
import reportWebVitals from './reportWebVitals';
import beforeInit from './beforeInit';
import Store from "./Store/index";

import '../node_modules/bootstrap/dist/css/bootstrap.min.css';
import './App.scss';
import './Firefox.scss';
import './investment.scss';
import './childModule.scss';
import './tenant.scss';
import '../node_modules/font-awesome/css/font-awesome.min.css';
import '../node_modules/jquery/dist/jquery.js';
import '../node_modules/bootstrap/dist/js/bootstrap.bundle.min.js';
import './i18n';
import '../src/fonts/GothamLight.ttf';
import '../src/fonts/GothamMedium.ttf';
import '../src/fonts/GothamBook.ttf';
import '../src/fonts/GothamBold.ttf';
import * as constants from './Utils/constants';
import * as Sentry from "@sentry/react";
import { isBannoServiceProvider } from './Utils/ServiceProviders/banno';
import { initIframeAutoResize } from './Utils/iframeAutoResize';

const translations = sessionStorage.getItem(constants.LOCALSTORAGE.translations);
const lang = sessionStorage.getItem(constants.LOCALSTORAGE.lang);

beforeInit(translations, lang);


const SENSITIVE_KEYS = [
  "password",
  "token",
  "access_token",
  "refresh_token",
  "authorization",
  "cookie",
  "secret",
  "api_key",
  "credit_card",
  "cvv",
  "otp",
  "phone",
  "email",
  "CardNumber",
  "ExpirationDate",
  "ExpirationDateDay",
  "ExpirationDateMonth",
  "ExpirationDateYear",
  "dateOfBirth",
  "dob",
  "taxId",
  "taxIdNumber",
  "cif",
  "cifnumber",
  "cifid",
  "membernumber",
  "memberno",
  "member_number",
  "externalaccountid",
  "externalid",
  "external_account_id",
];

// These fields are partially masked: last 4 chars visible, rest replaced with X.
// e.g.  "123456789" → "XXXXX6789"
const PARTIAL_MASK_KEYS = [
  "ssn",
  "socialsecuritynumber",
];

function partialMask(value) {
  const str = String(value);
  if (str.length <= 4) return str.replace(/./g, "X");
  return "X".repeat(str.length - 4) + str.slice(-4);
}

function sanitize(obj) {
  if (!obj || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(sanitize);
  }

  const cloned = {};

  Object.keys(obj).forEach((key) => {
    const lowerKey = key.toLowerCase();

    if (SENSITIVE_KEYS.some((k) => lowerKey.includes(k.toLowerCase()))) {
      cloned[key] = "[REDACTED]";
    } else if (PARTIAL_MASK_KEYS.some((k) => lowerKey.includes(k))) {
      const val = obj[key];
      cloned[key] = (val !== null && val !== undefined) ? partialMask(val) : val;
    } else {
      cloned[key] = sanitize(obj[key]);
    }
  });

  return cloned;
}

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV,

  integrations: [
    Sentry.browserTracingIntegration(),
  ],

  // Task #15380: browser-extension noise (e.g. MetaMask) is not our code and must not use the error quota
  ignoreErrors: [/MetaMask/i],
  denyUrls: [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
  ],

  beforeSend(event, hint) {
    const error = hint?.originalException;

    // Ignore 401
    if (error?.message?.includes("401")) {
      return null;
    }

    // Sanitize request data
    if (event.request) {
      event.request = sanitize(event.request);
    }

    // Sanitize extra data
    if (event.extra) {
      event.extra = sanitize(event.extra);
    }

    // Sanitize contexts
    if (event.contexts) {
      event.contexts = sanitize(event.contexts);
    }

    // Sanitize message string — may contain JSON-stringified objects with sensitive fields
    if (event.message) {
      SENSITIVE_KEYS.forEach((key) => {
        const regex = new RegExp(`("${key}"\\s*:\\s*)"[^"]*"`, "gi");
        event.message = event.message.replace(regex, '$1"[REDACTED]"');
      });
      PARTIAL_MASK_KEYS.forEach((key) => {
        const regex = new RegExp(`("${key}"\\s*:\\s*)"([^"]*)"`, "gi");
        event.message = event.message.replace(regex, (_, prefix, val) => `${prefix}"${partialMask(val)}"`);
      });
    }

    // Remove user info completely
    delete event.user;

    // Remove breadcrumbs if needed
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((b) => {
        let message = b.message;
        if (message) {
          SENSITIVE_KEYS.forEach((key) => {
            const regex = new RegExp(`("${key}"\\s*:\\s*)"[^"]*"`, "gi");
            message = message.replace(regex, '$1"[REDACTED]"');
          });
          PARTIAL_MASK_KEYS.forEach((key) => {
            const regex = new RegExp(`("${key}"\\s*:\\s*)"([^"]*)"`, "gi");
            message = message.replace(regex, (_, prefix, val) => `${prefix}"${partialMask(val)}"`);
          });
        }
        return { ...b, message, data: sanitize(b.data) };
      });
    }

    return event;
  },

  sendDefaultPii: false,
});

const AppWithSentry = Sentry.withErrorBoundary(App, {
  fallback: null,
});

document.addEventListener('click', function (e) {
  const tenantFullName = Store.getState()?.theme?.values?.tenantFullName;
  if (!isBannoServiceProvider(tenantFullName, false)) return;
  const anchor = e.target.closest('a[target="_blank"]');
  if (anchor && anchor.href) {
    e.preventDefault();
    window.open(anchor.href, '_blank');
  }
});

ReactDOM.render(
  <React.StrictMode>
    <StoreProvider store={Store}>
      <Suspense fallback={null}>
        <AppWithSentry />
      </Suspense>
    </StoreProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

initIframeAutoResize();

reportWebVitals();